// Thin wrapper around Telegram WebApp injected by telegram-web-app.js
// Falls back to no-op stubs in browser dev.

type ImpactStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
type NotificationType = 'error' | 'success' | 'warning';

declare global {
  interface Window {
    Telegram?: {
      WebApp: any;
    };
  }
}

export const tg = () => window.Telegram?.WebApp;

export function initTelegram() {
  const w = tg();
  if (!w) return;
  try {
    w.ready();
    w.expand();
    w.setHeaderColor?.('#0d0b14');
    w.setBackgroundColor?.('#0d0b14');
    w.enableClosingConfirmation?.();
  } catch {}
}

export function initData(): string {
  return tg()?.initData ?? '';
}

export function haptic(kind: ImpactStyle = 'light') {
  try {
    tg()?.HapticFeedback?.impactOccurred?.(kind);
  } catch {}
}

export function hapticNotify(kind: NotificationType) {
  try {
    tg()?.HapticFeedback?.notificationOccurred?.(kind);
  } catch {}
}

export function hapticSelection() {
  try {
    tg()?.HapticFeedback?.selectionChanged?.();
  } catch {}
}

export function showAlert(msg: string): Promise<void> {
  return new Promise((resolve) => {
    const w = tg();
    if (w?.showAlert) w.showAlert(msg, () => resolve());
    else {
      // eslint-disable-next-line no-alert
      alert(msg);
      resolve();
    }
  });
}

export function showConfirm(msg: string): Promise<boolean> {
  return new Promise((resolve) => {
    const w = tg();
    if (w?.showConfirm) w.showConfirm(msg, (ok: boolean) => resolve(ok));
    else resolve(window.confirm(msg));
  });
}

export function closeApp() {
  tg()?.close?.();
}

/** Lock the Mini App against vertical-swipe-to-close so the seat map can be
 * panned by dragging without yanking the whole sheet up. Bot API 7.7+. */
export function lockMiniAppViewport() {
  try {
    tg()?.disableVerticalSwipes?.();
    tg()?.expand?.();
  } catch {}
}

export function unlockMiniAppViewport() {
  try {
    tg()?.enableVerticalSwipes?.();
  } catch {}
}

export function showScanQrPopup(text?: string): Promise<string | null> {
  return new Promise((resolve) => {
    const w = tg();
    if (!w?.showScanQrPopup) {
      resolve(null);
      return;
    }
    const handler = (data: string) => {
      w.offEvent('qrTextReceived', handler);
      w.closeScanQrPopup();
      resolve(data);
    };
    w.onEvent('qrTextReceived', handler);
    w.showScanQrPopup({ text: text ?? 'Наведите камеру на QR билета' });
  });
}

export function platform(): string {
  return tg()?.platform ?? 'unknown';
}

export function openExternalLink(url: string) {
  const w = tg();
  if (w?.openLink) w.openLink(url);
  else window.open(url, '_blank');
}

/** Download a file through the Telegram client (Bot API 8.0+).
 * Resolves true once the user accepts the download popup,
 * false if the client is too old or the user declined. */
export function downloadFile(url: string, fileName: string): Promise<boolean> {
  return new Promise((resolve) => {
    const w = tg();
    if (!w?.downloadFile) {
      resolve(false);
      return;
    }
    try {
      w.downloadFile({ url, file_name: fileName }, (accepted: boolean) => resolve(accepted));
    } catch {
      resolve(false);
    }
  });
}

export function copyToClipboard(text: string) {
  haptic('medium');
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  } else {
    fallbackCopy(text);
  }
}

function fallbackCopy(text: string) {
  const ta = document.createElement('textarea');
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand('copy'); } catch {}
  document.body.removeChild(ta);
}
